/**
 * App router — rule42-router admin UI
 *
 * The router is a single-purpose AI gateway, so its admin surface covers only
 * the gateway itself. Identity (orgs, users, accounts) and money (the credit
 * ledger) both live in auth.rule42.ai; neither is administered from here. The
 * money half used to live in a separate billing service, which is retired.
 *
 * Route structure:
 *   /                                   → redirect to /operator
 *   /login                              → Login (OIDC)
 *   /register                           → hands off to auth.rule42.ai
 *   /auth/callback                      → AuthCallback
 *   /org-select                         → OrgSelect
 *   /forbidden                          → 403
 *
 *   -- Gateway operator --
 *   /operator                           → Overview
 *   /operator/settings/model-gateway    → Model Gateway (catalog, providers, markup)
 *   /operator/settings/preflight-checks → Pre-flight Checks (guardrails, finance)
 *   /operator/developer-guide           → Developer Guide
 *   /operator/api-keys                  → API Keys + their rate limits
 *   /operator/monitoring                → Monitoring
 */

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/auth.js';
import { OrgProvider } from './contexts/org.js';
import { ThemeProvider } from './contexts/theme.js';
import { ToastProvider } from './ui/index.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { CloudShell } from './components/layout/CloudShell.js';
import { PageErrorBoundary } from './components/PageErrorBoundary.js';
import { LoginPage } from './pages/Login.js';
import { RegisterPage } from './pages/Register.js';
import { AuthCallbackPage } from './pages/AuthCallback.js';
import { OrgSelectPage } from './pages/OrgSelect.js';
import { ForbiddenPage } from './pages/ForbiddenPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';

// Gateway operator pages
import { OperatorDashboardPage } from './pages/OperatorDashboardPage.js';
import { PlatformModelGatewayPage } from './pages/PlatformModelGatewayPage.js';
import { PlatformPreflightChecksPage } from './pages/PlatformPreflightChecksPage.js';
import { PlatformApiKeysPage } from './pages/PlatformApiKeysPage.js';
import { PlatformMonitoringPage } from './pages/PlatformMonitoringPage.js';
import { PlatformDeveloperGuidePage } from './pages/PlatformDeveloperGuidePage.js';

const PLATFORM_PERMISSIONS = ['router.admin', 'platform.admin'];

function ProtectedShell({ children, requiredPermissions }: { children: React.ReactNode; requiredPermissions?: string[] }) {
  // Keyed by path: every route renders a ProtectedShell in the same place, so React
  // reuses one boundary across pages and a caught error would follow the person
  // to the next page they open.
  const { pathname } = useLocation();
  return (
    <ProtectedRoute requiredPermissions={requiredPermissions}>
      <CloudShell><PageErrorBoundary key={pathname}>{children}</PageErrorBoundary></CloudShell>
    </ProtectedRoute>
  );
}

export function App() {
  return (
    <ThemeProvider>
    <ToastProvider>
    <AuthProvider>
      <BrowserRouter>
        <OrgProvider>
          <Routes>
            {/* Public */}
            <Route path="/login"          element={<LoginPage />} />
            <Route path="/register"       element={<RegisterPage />} />
            <Route path="/auth/callback"  element={<AuthCallbackPage />} />
            <Route path="/org-select"     element={<OrgSelectPage />} />
            <Route path="/forbidden"      element={<ForbiddenPage />} />

            {/* Gateway operator */}
            <Route path="/"                                   element={<ProtectedShell requiredPermissions={PLATFORM_PERMISSIONS}><Navigate to="/operator" replace /></ProtectedShell>} />
            <Route path="/operator"                           element={<ProtectedShell requiredPermissions={PLATFORM_PERMISSIONS}><OperatorDashboardPage /></ProtectedShell>} />
            <Route path="/operator/settings/model-gateway"    element={<ProtectedShell requiredPermissions={PLATFORM_PERMISSIONS}><PlatformModelGatewayPage /></ProtectedShell>} />
            <Route path="/operator/settings/preflight-checks" element={<ProtectedShell requiredPermissions={PLATFORM_PERMISSIONS}><PlatformPreflightChecksPage /></ProtectedShell>} />
            <Route path="/operator/developer-guide"           element={<ProtectedShell requiredPermissions={PLATFORM_PERMISSIONS}><PlatformDeveloperGuidePage /></ProtectedShell>} />
            <Route path="/operator/api-keys"                  element={<ProtectedShell requiredPermissions={PLATFORM_PERMISSIONS}><PlatformApiKeysPage /></ProtectedShell>} />
            <Route path="/operator/monitoring"                element={<ProtectedShell requiredPermissions={PLATFORM_PERMISSIONS}><PlatformMonitoringPage /></ProtectedShell>} />

            <Route path="*"               element={<NotFoundPage />} />
          </Routes>
        </OrgProvider>
      </BrowserRouter>
    </AuthProvider>
    </ToastProvider>
    </ThemeProvider>
  );
}
